import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import type { CSSProperties } from 'react';
import { useMemo } from 'react';
import type { PopupProps } from 'semantic-ui-react';
import { Popup } from 'semantic-ui-react';
import StackIcon from 'tech-stack-icons';

import { useIsDarkMode } from '@components/dark-mode/dark-mode-provider';
import { DarkReaderIgnore } from '@components/dark-mode/dark-reader-ignore';

import {
  FA_ICON_BY_PACKAGE_TYPE,
  LABEL_BY_PACKAGE_TYPE,
  LUCIDE_ICON_BY_PACKAGE_TYPE,
  MFIZZ_ICON_BY_PACKAGE_TYPE,
  STACK_ICON_BY_PACKAGE_TYPE,
  resolvePackageType,
} from './package-type.const';
import s from './package-type-icon.scss';

const DEFAULT_SIZE = '2rem';

/**
 * Types that can render as a single-color silhouette via a monochrome mask
 * asset (see `package-type-icon.scss`).
 */
const MONOCHROME_MASK_TYPES = new Set(['model']);

/**
 * Per-variant ratios derived from the original 2rem-tuned values in
 * `package-type-icon.scss`. The component uses them to scale Mfizz/FA/Lucide
 * font glyphs proportionally to the requested icon-box size, so consumers
 * only need to pass a single `size` value.
 */
const SIZING_RATIOS = {
  mfizzFontSize: 1.85 / 2,
  faFontSize: 1.75 / 2,
  lucideFontSize: 1.75 / 2,
};

const sizingStyle = (size: string): CSSProperties =>
  ({
    '--package-type-icon-size': size,
    '--package-type-icon-mfizz-font-size': `calc(${size} * ${SIZING_RATIOS.mfizzFontSize})`,
    '--package-type-icon-fa-font-size': `calc(${size} * ${SIZING_RATIOS.faFontSize})`,
    '--package-type-icon-lucide-font-size': `calc(${size} * ${SIZING_RATIOS.lucideFontSize})`,
  }) as CSSProperties;

export interface PackageTypeIconProps {
  /**
   * Renders the icon in its faded grayscale variant, for placement inside
   * disabled controls whose `color`-based fading the SVG icons ignore.
   */
  disabled?: boolean;
  /**
   * Renders the icon as a single-color silhouette that follows
   * `currentcolor`, for contexts that color icons semantically (e.g. menu
   * states). Takes precedence over `disabled` — the surrounding text color
   * conveys the state. Types without a monochrome mask asset fall back to
   * their standard rendering.
   */
  monochrome?: boolean;
  /**
   * Forwarded onto the icon's root element so consumers can layer additional
   * styling on top of the built-in sizing.
   */
  className?: string;
  /**
   * Forwarded onto the label `Popup` — pass `{ disabled: true }` to suppress
   * the tooltip when the surrounding context already labels the icon.
   */
  popup?: PopupProps;
  /**
   * CSS length for the icon's box dimensions. Internally scales the font
   * size of glyph-based variants (Mfizz/FA/Lucide) so all icon families
   * render at a consistent visual size.
   */
  size?: string;
  type: string;
}

/**
 * Renders an icon for the given package type.
 * Falls back to a cube icon for unknown types.
 */
export const PackageTypeIcon = ({
  className,
  disabled = false,
  monochrome = false,
  popup,
  size = DEFAULT_SIZE,
  type: rawType,
}: PackageTypeIconProps) => {
  useStyles(s);

  const isDarkMode = useIsDarkMode();
  const type = resolvePackageType(rawType);
  const label = LABEL_BY_PACKAGE_TYPE[type] ?? type;
  const style = useMemo(() => sizingStyle(size), [size]);

  const icon = useMemo(() => {
    switch (true) {
      case monochrome && MONOCHROME_MASK_TYPES.has(type):
        return (
          <i
            aria-label={label}
            className={classNames(s.root, s.monochrome, className)}
            data-type={type}
            style={style}
          />
        );

      case type in STACK_ICON_BY_PACKAGE_TYPE: {
        const iconName = STACK_ICON_BY_PACKAGE_TYPE[type];

        const variant = (() => {
          if (disabled) return 'grayscale';
          return isDarkMode ? 'dark' : 'light';
        })();

        return (
          <span>
            <DarkReaderIgnore
              aria-label={label}
              className={classNames(s.root, disabled && s.disabled, className)}
              data-type={type}
              style={style}
            >
              <StackIcon name={iconName} variant={variant} />
            </DarkReaderIgnore>
          </span>
        );
      }

      case type in LUCIDE_ICON_BY_PACKAGE_TYPE: {
        const LucideIcon = LUCIDE_ICON_BY_PACKAGE_TYPE[type];
        return (
          <LucideIcon
            aria-label={label}
            className={classNames(
              s.root,
              s.lucideIcon,
              disabled && s.disabled,
              className,
            )}
            data-type={type}
            style={style}
          />
        );
      }

      case type in FA_ICON_BY_PACKAGE_TYPE:
        return (
          <i
            aria-label={label}
            className={classNames(
              s.root,
              FA_ICON_BY_PACKAGE_TYPE[type],
              s.faIcon,
              disabled && s.disabled,
              className,
            )}
            data-type={type}
            style={style}
          />
        );

      case type in MFIZZ_ICON_BY_PACKAGE_TYPE:
        return (
          <i
            aria-label={label}
            className={classNames(
              s.root,
              MFIZZ_ICON_BY_PACKAGE_TYPE[type],
              s.mfizzIcon,
              disabled && s.disabled,
              className,
            )}
            data-type={type}
            style={style}
          />
        );

      default:
        return (
          <i
            aria-label={label}
            className={classNames(
              s.root,
              'cube icon',
              s.faIcon,
              disabled && s.disabled,
              className,
            )}
            data-type={type}
            style={style}
          />
        );
    }
  }, [className, disabled, isDarkMode, label, monochrome, style, type]);

  return (
    <Popup
      basic
      content={label}
      position="top left"
      role="tooltip"
      trigger={icon}
      {...popup}
    />
  );
};
